import dynamic from "next/dynamic";

import { useCallback } from "react";

import type { Insight } from "models/insight";
import { twMerge } from "tailwind-merge";
import { useToggle } from "usehooks-ts";

import useNotification from "components/Notification/useNotification";
import useAuthentication from "hooks/useAuthentication";
import { fetchApi } from "utils/baseFetcher";

const Modal = dynamic(() => import("react-modal"), { ssr: false });

const RemovePaywallButton = ({
  insight,
  onUpdate,
  className,
}: {
  insight: Insight;
  onUpdate?: () => void;
  className?: string;
}) => {
  const { account: loggedInAccount } = useAuthentication();
  const { setNotification } = useNotification();

  const canRemovePaywall =
    !insight.isPublic && insight.account?.id === loggedInAccount?.id;

  const [showPrompt, togglePrompt] = useToggle();

  const removePaywall = useCallback(() => {
    fetchApi(`/api/v2/insights/${insight.id}`, {
      method: "PATCH",
      body: JSON.stringify({
        data: {
          id: insight.id,
          type: "insights",
          attributes: { "is-public": true },
        },
      }),
    }).then(() => {
      setNotification("Paywall has been removed successfully.", "success");
      togglePrompt();
      onUpdate?.();
    });
  }, [insight.id, onUpdate, togglePrompt, setNotification]);

  if (!canRemovePaywall) return null;

  return (
    <>
      <button
        className={
          className ||
          twMerge(
            "outline-none bg-white",
            "rounded-full border border-solid border-smart-teal",
            "px-[10px] py-[6px]",
            "text-smart-teal text-[12px] leading-[12px]"
          )
        }
        onClick={togglePrompt}
      >
        Remove Paywall
      </button>
      {showPrompt && (
        <Modal
          portalClassName="z-[1999]"
          overlayClassName={
            "z-[1999] fixed inset-0 bg-[rgba(31,31,32,.77)] overflow-y-auto flex items-center"
          }
          className={twMerge(
            "relative",
            "w-[500px] mx-auto",
            "outline-none rounded-xl bg-white",
            "flex flex-col",
            "px-[25px] py-[30px]"
          )}
          isOpen
          onAfterOpen={() => {
            document.body.style.overflowY = "hidden";
          }}
          onAfterClose={() => {
            document.body.style.overflowY = "";
          }}
          onRequestClose={togglePrompt}
        >
          <button
            className="absolute top-[-10px] right-[5px] border-0 bg-transparent text-[40px] font-light text-shady-gray"
            onClick={togglePrompt}
          >
            ×
          </button>
          <h4 className="text-[22px] font-light mb-[15px]">Remove Paywall?</h4>
          <p>
            Removing the paywall will allow non-subscribers to view this
            particular Insight in full. The benefit to you is increased exposure
            and visibility when your Insight is shared via social channels and
            other marketing activities.
          </p>
          <p>
            Please note you will not receive any QVA points for this particular
            Insight, and you cannot turn the paywall back on later.
          </p>
          <div className="w-full">
            <button
              className={twMerge(
                "outline-none bg-white",
                "rounded-full border border-solid border-active-black",
                "px-[15px] py-[11px]",
                "text-active-black text-[15px] leading-[15px]"
              )}
              onClick={togglePrompt}
            >
              Cancel
            </button>
            <button
              className={twMerge(
                "float-right",
                "outline-none bg-jade",
                "rounded-full border-none",
                "px-[15px] py-[11px]",
                "text-white text-[15px] leading-[15px]"
              )}
              onClick={removePaywall}
            >
              Remove
            </button>
          </div>
        </Modal>
      )}
    </>
  );
};

export default RemovePaywallButton;
